// src/views/configuracoes/ConfiguracoesList.jsx
import React, { useState, useEffect, useCallback } from "react";
import emailService from "../../services/emailService";
import ConfirmModal from "../../components/ui/ConfirmModal";

const fmtDt = d => { try { return new Date(d).toLocaleString("pt-BR"); } catch { return d || "—"; } };

// Cada chave aqui é uma flag em config_email; o `tipo` corresponde ao
// `tpNotificacao` salvo em email_log (usado para filtros de log/reenvio).
const NOTIF_LABELS = {
  notifBoleto:            { label:"Boleto Gerado",            icon:"bi-receipt",                desc:"Envia e-mail ao condômino quando um boleto é gerado.",                       tipo:"BoletoGerado" },
  notifBoletoVencido:     { label:"Boleto Vencido",           icon:"bi-exclamation-circle",     desc:"Envia alerta quando um boleto vence sem pagamento.",                          tipo:"BoletoVencido" },
  notifLembrete3dias:     { label:"Lembrete (3 dias antes)",  icon:"bi-bell",                   desc:"Job diário às 08h envia lembrete 3 dias antes do vencimento.",                tipo:"LembretePagamento" },
  notifReserva:           { label:"Reservas",                 icon:"bi-calendar-check",         desc:"Confirmação e cancelamento de reservas de áreas comuns.",                     tipo:"ReservaConfirmada" },
  notifOcorrencia:        { label:"Ocorrências",              icon:"bi-exclamation-triangle",   desc:"Notifica o condômino a cada atualização de status.",                          tipo:"OcorrenciaAtualizada" },
  notifComunicado:        { label:"Comunicados",              icon:"bi-megaphone",              desc:"Envia comunicados publicados por e-mail aos destinatários.",                  tipo:"ComunicadoPublicado" },
  notifRenegociacao:      { label:"Renegociação",             icon:"bi-handshake",              desc:"Notifica quando um acordo de renegociação é criado.",                         tipo:"RenegociacaoCriada" },
  notifEncomendaChegada:  { label:"Encomenda Chegou",         icon:"bi-box-seam",               desc:"Avisa o condômino quando uma encomenda chega na portaria.",                   tipo:"EncomendaChegada" },
  notifEncomendaRetirada: { label:"Encomenda Retirada",       icon:"bi-check2-circle",          desc:"Confirma ao condômino quando sua encomenda é retirada.",                      tipo:"EncomendaRetirada" },
  notifEntradaVisitante:  { label:"Entrada de Visitante",     icon:"bi-person-door",            desc:"Avisa o condômino quando um visitante chega para visitá-lo.",                 tipo:"EntradaVisitante" },
  notifFechamento:        { label:"Fechamento Mensal",        icon:"bi-file-earmark-bar-graph", desc:"Notifica os condôminos quando o demonstrativo financeiro mensal é publicado.",tipo:"FechamentoMensal" },
};

const SEGURANCA_LABELS = { none:"Nenhuma (porta 25)", ssl:"SSL (porta 465)", tls:"TLS (porta 587)", starttls:"STARTTLS (porta 587)" };
const SEGURANCA_PORTA  = { none:25, ssl:465, tls:587, starttls:587 };

const EMPTY_CFG = {
  host:"", porta:"587", seguranca:"tls",
  usuario:"", senha:"", nmRemetente:"Horizon eCondo", emailRemetente:"",
  ativo:1,
  notifBoleto:1, notifBoletoVencido:1, notifLembrete3dias:1,
  notifReserva:1, notifOcorrencia:1, notifComunicado:1, notifRenegociacao:1,
  notifEncomendaChegada:1, notifEncomendaRetirada:1, notifEntradaVisitante:1, notifFechamento:1,
};

// Banner UX reutilizável
function Banner({ tipo="info", children, onClose }) {
  const cores = {
    error:   { bg:"#FEE2E2", border:"#FECACA", color:"#991B1B", icon:"bi-exclamation-octagon"  },
    success: { bg:"#DCFCE7", border:"#BBF7D0", color:"#166534", icon:"bi-check-circle"          },
    info:    { bg:"#EFF6FF", border:"#BFDBFE", color:"#1D4ED8", icon:"bi-info-circle"           },
    warning: { bg:"#FEF3C7", border:"#FDE68A", color:"#92400E", icon:"bi-exclamation-triangle"  },
  };
  const c = cores[tipo] || cores.info;
  if (!children) return null;
  return (
    <div style={{
      background:c.bg, border:`1px solid ${c.border}`, color:c.color,
      borderRadius:8, padding:"10px 14px", marginBottom:12,
      display:"flex", alignItems:"center", gap:10, fontSize:13.5,
    }}>
      <i className={`bi ${c.icon}`} style={{ fontSize:18 }}/>
      <div style={{ flex:1 }}>{children}</div>
      {onClose && (
        <button className="btn btn-sm" style={{ padding:"2px 8px", background:"transparent", color:c.color }} onClick={onClose}>
          <i className="bi bi-x-lg"/>
        </button>
      )}
    </div>
  );
}

export default function ConfiguracoesList() {
  const [aba,             setAba]             = useState("smtp");
  const [form,            setForm]            = useState(EMPTY_CFG);
  const [originalCfg,     setOriginalCfg]     = useState(null); // para detectar "ainda não configurado"
  const [senhaConfigurada,setSenhaConfigurada]= useState(false);
  const [loading,         setLoading]         = useState(true);
  const [saving,          setSaving]          = useState(false);
  const [testing,         setTesting]         = useState(false);
  const [testResult,      setTestResult]      = useState(null);
  const [showSenha,       setShowSenha]       = useState(false);

  const [pageError,       setPageError]       = useState("");
  const [pageSuccess,     setPageSuccess]     = useState("");

  // Log
  const [log,             setLog]             = useState([]);
  const [loadingLog,      setLoadingLog]      = useState(false);
  const [reenviandoId,    setReenviandoId]    = useState(null);
  const [filtroLog,       setFiltroLog]       = useState({ tpNotificacao:"", status:"", busca:"" });

  // Stats
  const [stats,           setStats]           = useState(null);

  // Confirmação global
  const [confirmacao, setConfirmacao] = useState(null);
  const askConfirm = (opts) => new Promise(resolve => setConfirmacao({ ...opts, resolve }));

  const carregar = useCallback(async () => {
    setLoading(true);
    setPageError("");
    try {
      const r = await emailService.buscarConfig();
      if (r.data) {
        setForm({ ...EMPTY_CFG, ...r.data, senha:"" });
        setOriginalCfg(r.data);
        setSenhaConfigurada(r.data.senhaConfigurada || false);
      } else {
        setForm(EMPTY_CFG);
        setOriginalCfg(null);
        setSenhaConfigurada(false);
      }
      const s = await emailService.stats();
      setStats(s);
    } catch (e) {
      setPageError(`Erro ao carregar configuração: ${e.message}`);
    }
    setLoading(false);
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const carregarLog = useCallback(async () => {
    setLoadingLog(true);
    try {
      const r = await emailService.listarLog({ ...filtroLog, limit:50 });
      setLog(r.data || []);
    } catch (e) {
      setPageError(`Erro ao carregar log: ${e.message}`);
    }
    setLoadingLog(false);
  }, [filtroLog]);

  useEffect(() => {
    if (aba === "log") carregarLog();
  }, [aba, carregarLog]);

  const set = (f, v) => setForm(p => ({ ...p, [f]:v }));

  const validarForm = () => {
    if (!form.host?.trim())            return "Servidor SMTP (Host) é obrigatório.";
    if (!form.usuario?.trim())         return "Usuário/E-mail é obrigatório.";
    if (!form.emailRemetente?.trim())  return "E-mail Remetente é obrigatório.";
    const reEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!reEmail.test(form.usuario.trim()))        return "Usuário/E-mail inválido.";
    if (!reEmail.test(form.emailRemetente.trim())) return "E-mail Remetente inválido.";
    if (!senhaConfigurada && !form.senha?.trim()) return "Senha SMTP é obrigatória (primeira configuração).";
    const portaNum = parseInt(form.porta);
    if (isNaN(portaNum) || portaNum < 1 || portaNum > 65535) return "Porta inválida (1–65535).";
    return null;
  };

  const handleSalvar = async () => {
    const erro = validarForm();
    if (erro) { setPageError(erro); return; }
    setSaving(true);
    setTestResult(null);
    setPageError("");
    setPageSuccess("");
    try {
      await emailService.salvarConfig(form);
      await carregar();
      setPageSuccess("✅ Configuração salva com sucesso!");
    } catch (e) { setPageError(`Falha ao salvar: ${e.message}`); }
    setSaving(false);
  };

  const handleTestar = async () => {
    if (!form.host) { setPageError("Preencha o servidor SMTP antes de testar."); return; }
    setTesting(true);
    setTestResult(null);
    setPageError("");
    try {
      const r = await emailService.testar(form);
      setTestResult({ ok: r.success, msg: r.message });
    } catch (e) { setTestResult({ ok: false, msg: e.message }); }
    setTesting(false);
  };

  const handleSeguranca = (v) => {
    setForm(p => ({ ...p, seguranca:v, porta: SEGURANCA_PORTA[v] || p.porta }));
  };

  const handleReenviar = async (id) => {
    const ok = await askConfirm({
      titulo: "Reenviar e-mail",
      mensagem: "Deseja reenviar este e-mail? O destinatário receberá uma nova cópia.",
      textoOk: "Reenviar",
    });
    if (!ok) return;
    setReenviandoId(id);
    setPageError("");
    setPageSuccess("");
    try {
      const r = await emailService.reenviar(id);
      setPageSuccess(r.message || "E-mail reenviado.");
      carregarLog();
    } catch (e) { setPageError(`Falha no reenvio: ${e.message}`); }
    setReenviandoId(null);
  };

  // ── Status da configuração para o banner UX ────────────────────────────
  const semConfig    = !loading && !originalCfg;
  const configInativa= !loading && originalCfg && !(originalCfg.ativo === 1 || originalCfg.ativo === true);
  const semSenha     = !loading && originalCfg && !senhaConfigurada;

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">
            <i className="bi bi-gear me-2" style={{ color:"var(--primary)", fontSize:22 }}/>
            Configurações do Sistema
          </h1>
          <p className="page-subtitle">Configurações de e-mail, notificações e sistema</p>
        </div>
      </div>

      {/* Modal de confirmação global */}
      {confirmacao && (
        <ConfirmModal
          titulo={confirmacao.titulo}
          mensagem={confirmacao.mensagem}
          textoOk={confirmacao.textoOk}
          perigo={confirmacao.perigo}
          onConfirm={()=>{ confirmacao.resolve(true); setConfirmacao(null); }}
          onCancel={()=>{ confirmacao.resolve(false); setConfirmacao(null); }}
        />
      )}

      {/* Banners de estado */}
      <Banner tipo="error"   onClose={() => setPageError("")}>{pageError}</Banner>
      <Banner tipo="success" onClose={() => setPageSuccess("")}>{pageSuccess}</Banner>

      {!loading && semConfig && (
        <Banner tipo="info">
          <strong>SMTP ainda não configurado.</strong> Preencha os dados do servidor de e-mail abaixo
          para começar a enviar boletos, lembretes e demais notificações automaticamente.
        </Banner>
      )}
      {!loading && configInativa && (
        <Banner tipo="warning">
          <strong>SMTP desativado.</strong> O sistema não enviará e-mails até que você ative a configuração.
        </Banner>
      )}
      {!loading && semSenha && (
        <Banner tipo="warning">
          <strong>Senha SMTP não configurada.</strong> Edite a configuração e informe a senha para que os envios funcionem.
        </Banner>
      )}

      {/* Stats rápidos */}
      {stats && (
        <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:12, marginBottom:16 }}>
          {[
            { label:"Enviados Hoje",  val:stats.hoje?.enviados || 0,    color:"#166534", bg:"#DCFCE7", icon:"bi-check-circle" },
            { label:"Erros Hoje",     val:stats.hoje?.erros || 0,       color:"#991B1B", bg:"#FEE2E2", icon:"bi-x-circle"      },
            { label:"Total no Mês",   val:stats.cfg?.totalEnviados || 0,color:"#1D4ED8", bg:"#EFF6FF", icon:"bi-envelope"     },
            { label:"Último Envio",   val:stats.cfg?.dtUltimoEnvio ? fmtDt(stats.cfg.dtUltimoEnvio).split(" ")[0] : "—",
              color:"#6D28D9", bg:"#F5F3FF", icon:"bi-clock" },
          ].map(s => (
            <div key={s.label} style={{
              background:"var(--bg-surface)", borderRadius:10, padding:"14px 16px",
              border:"1px solid var(--gray-200)", display:"flex", gap:12, alignItems:"center",
            }}>
              <div style={{ width:36, height:36, borderRadius:8, background:s.bg, display:"flex", alignItems:"center", justifyContent:"center" }}>
                <i className={`bi ${s.icon}`} style={{ color:s.color, fontSize:17 }}/>
              </div>
              <div>
                <div style={{ fontWeight:700, fontSize:18, color:"var(--gray-800)" }}>{s.val}</div>
                <div style={{ fontSize:11, color:"var(--gray-400)" }}>{s.label}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="card">
        {/* Abas */}
        <div style={{ borderBottom:"1px solid var(--gray-200)", padding:"0 16px", display:"flex", gap:4 }}>
          {[
            { id:"smtp",  label:"Configuração SMTP", icon:"bi-envelope-gear" },
            { id:"notif", label:"Notificações",       icon:"bi-bell-fill"     },
            { id:"log",   label:"Log de Envios",      icon:"bi-list-ul"       },
          ].map(t => (
            <button key={t.id} className="btn border-0 d-flex align-items-center gap-2"
              style={{
                fontSize:13, padding:"12px 14px", borderRadius:0,
                borderBottom:aba === t.id ? "2px solid var(--primary)" : "2px solid transparent",
                fontWeight:aba === t.id ? 600 : 400,
                color:aba === t.id ? "var(--primary)" : "var(--gray-500)",
                background:"transparent",
              }}
              onClick={() => setAba(t.id)}>
              <i className={`bi ${t.icon}`}/>{t.label}
            </button>
          ))}
        </div>

        {/* ── ABA SMTP ── */}
        {aba === "smtp" && (
          <div style={{ padding:20 }}>
            {loading
              ? <div className="text-center py-4"><span className="spinner-border spinner-border-sm text-primary"/></div>
              : <>
                <div className="form-section">
                  <div className="form-section-title"><i className="bi bi-server"/> Servidor SMTP</div>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label">Servidor SMTP (Host) <span className="required-mark">*</span></label>
                      <input className="form-control" value={form.host}
                        onChange={e => set("host", e.target.value)}
                        placeholder="Ex: smtp.gmail.com | smtp.office365.com"/>
                      <small style={{ fontSize:11, color:"var(--gray-400)" }}>
                        Gmail: smtp.gmail.com · Outlook: smtp.office365.com · SendGrid: smtp.sendgrid.net
                      </small>
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Segurança</label>
                      <select className="form-select" value={form.seguranca}
                        onChange={e => handleSeguranca(e.target.value)}>
                        {Object.entries(SEGURANCA_LABELS).map(([v, l]) => (
                          <option key={v} value={v}>{l}</option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Porta</label>
                      <input className="form-control" type="number" value={form.porta}
                        onChange={e => set("porta", e.target.value)}/>
                    </div>
                  </div>
                </div>

                <div className="form-section">
                  <div className="form-section-title"><i className="bi bi-person-lock"/> Autenticação</div>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label">Usuário / E-mail <span className="required-mark">*</span></label>
                      <input className="form-control" type="email" value={form.usuario}
                        onChange={e => set("usuario", e.target.value)}
                        placeholder="seu@email.com"/>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">
                        Senha {senhaConfigurada && (
                          <span style={{ fontSize:11, color:"#166534", marginLeft:6 }}>
                            <i className="bi bi-check-circle-fill"/> Configurada
                          </span>
                        )}
                      </label>
                      <div className="input-group">
                        <input className="form-control"
                          type={showSenha ? "text" : "password"}
                          value={form.senha}
                          onChange={e => set("senha", e.target.value)}
                          autoComplete="new-password"
                          placeholder={senhaConfigurada ? "Deixe em branco para manter a senha atual" : "Informe a senha SMTP"}/>
                        <button className="btn btn-outline-secondary" type="button"
                          onClick={() => setShowSenha(p => !p)}>
                          <i className={`bi bi-eye${showSenha ? "-slash" : ""}`}/>
                        </button>
                      </div>
                      {form.usuario.includes("gmail") && (
                        <small style={{ fontSize:11, color:"#C2410C" }}>
                          <i className="bi bi-exclamation-triangle me-1"/>
                          Gmail: use uma <strong>Senha de App</strong> (não a senha normal).
                          <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer" style={{ marginLeft:4 }}>
                            Gerar senha de app →
                          </a>
                        </small>
                      )}
                    </div>
                  </div>
                </div>

                <div className="form-section">
                  <div className="form-section-title"><i className="bi bi-envelope"/> Remetente</div>
                  <div className="row g-3">
                    <div className="col-md-5">
                      <label className="form-label">Nome do Remetente</label>
                      <input className="form-control" value={form.nmRemetente}
                        onChange={e => set("nmRemetente", e.target.value)}
                        placeholder="Ex: Horizon eCondo | Parque das Flores"/>
                    </div>
                    <div className="col-md-5">
                      <label className="form-label">E-mail Remetente <span className="required-mark">*</span></label>
                      <input className="form-control" type="email" value={form.emailRemetente}
                        onChange={e => set("emailRemetente", e.target.value)}
                        placeholder="noreply@seucondominio.com.br"/>
                    </div>
                    <div className="col-md-2">
                      <label className="form-label">Ativo</label>
                      <div className="form-check form-switch mt-2">
                        <input className="form-check-input" type="checkbox" id="smtpAtivo"
                          checked={form.ativo === 1 || form.ativo === true}
                          onChange={e => set("ativo", e.target.checked ? 1 : 0)}/>
                        <label className="form-check-label" htmlFor="smtpAtivo">
                          {form.ativo ? "Sim" : "Não"}
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Resultado do teste */}
                {testResult && (
                  <div style={{
                    background:testResult.ok ? "#DCFCE7" : "#FEE2E2",
                    border:`1px solid ${testResult.ok ? "#BBF7D0" : "#FECACA"}`,
                    borderRadius:8, padding:"12px 16px", marginBottom:16,
                    display:"flex", alignItems:"center", gap:10, fontSize:13,
                  }}>
                    <i className={`bi ${testResult.ok ? "bi-check-circle-fill" : "bi-x-circle-fill"}`}
                      style={{ color:testResult.ok ? "#166534" : "#991B1B", fontSize:18 }}/>
                    <span style={{ color:testResult.ok ? "#166534" : "#991B1B", fontWeight:500 }}>
                      {testResult.msg}
                    </span>
                  </div>
                )}

                {/* Ações */}
                <div className="d-flex gap-2 justify-content-end">
                  <button className="btn btn-outline-secondary" onClick={handleTestar} disabled={testing || saving || !form.host}>
                    {testing
                      ? <><span className="spinner-border spinner-border-sm me-2"/>Testando...</>
                      : <><i className="bi bi-send me-2"/>Testar Conexão</>}
                  </button>
                  <button className="btn btn-primary" onClick={handleSalvar} disabled={saving || testing}>
                    {saving
                      ? <><span className="spinner-border spinner-border-sm me-2"/>Salvando...</>
                      : <><i className="bi bi-check-circle me-2"/>Salvar Configuração</>}
                  </button>
                </div>
              </>
            }
          </div>
        )}

        {/* ── ABA NOTIFICAÇÕES ── */}
        {aba === "notif" && (
          <div style={{ padding:20 }}>
            <div className="form-section">
              <div className="form-section-title"><i className="bi bi-bell"/> Notificações Ativas</div>
              <p style={{ fontSize:13, color:"var(--gray-500)", marginBottom:16 }}>
                Configure quais eventos disparam e-mails automáticos. As notificações só são enviadas se o SMTP estiver configurado e ativo.
              </p>
              <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
                {Object.entries(NOTIF_LABELS).map(([field, info]) => (
                  <div key={field} style={{
                    display:"flex", justifyContent:"space-between", alignItems:"center",
                    padding:"14px 16px", background:"var(--gray-50)", borderRadius:10,
                    border:"1px solid var(--gray-200)",
                  }}>
                    <div style={{ display:"flex", alignItems:"center", gap:12 }}>
                      <div style={{
                        width:36, height:36, borderRadius:8,
                        background:form[field] ? "#EFF6FF" : "var(--gray-100)",
                        display:"flex", alignItems:"center", justifyContent:"center",
                      }}>
                        <i className={`bi ${info.icon}`}
                          style={{ color:form[field] ? "var(--primary)" : "var(--gray-400)", fontSize:17 }}/>
                      </div>
                      <div>
                        <div style={{ fontWeight:600, fontSize:13.5, color:"var(--gray-800)" }}>
                          {info.label}
                        </div>
                        <div style={{ fontSize:12, color:"var(--gray-400)" }}>{info.desc}</div>
                      </div>
                    </div>
                    <div className="form-check form-switch mb-0">
                      <input className="form-check-input" type="checkbox"
                        style={{ width:48, height:24 }}
                        checked={form[field] === 1 || form[field] === true}
                        onChange={e => set(field, e.target.checked ? 1 : 0)}/>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="d-flex justify-content-end">
              <button className="btn btn-primary" onClick={handleSalvar} disabled={saving}>
                {saving
                  ? <><span className="spinner-border spinner-border-sm me-2"/>Salvando...</>
                  : <><i className="bi bi-check-circle me-2"/>Salvar Notificações</>}
              </button>
            </div>
          </div>
        )}

        {/* ── ABA LOG ── */}
        {aba === "log" && (
          <>
            {/* Filtros */}
            <div style={{
              padding:"10px 16px", borderBottom:"1px solid var(--gray-100)",
              display:"flex", gap:8, flexWrap:"wrap", alignItems:"center",
            }}>
              <select className="form-select form-select-sm" style={{ width:"auto" }}
                value={filtroLog.tpNotificacao}
                onChange={e => setFiltroLog(p => ({ ...p, tpNotificacao:e.target.value }))}>
                <option value="">Todos os tipos</option>
                {Object.entries(NOTIF_LABELS).map(([k, info]) => (
                  <option key={k} value={info.tipo}>{info.label}</option>
                ))}
                <option value="Outro">Outros</option>
              </select>
              <select className="form-select form-select-sm" style={{ width:"auto" }}
                value={filtroLog.status}
                onChange={e => setFiltroLog(p => ({ ...p, status:e.target.value }))}>
                <option value="">Todos os status</option>
                <option value="Enviado">Enviado</option>
                <option value="Erro">Erro</option>
                <option value="Pendente">Pendente</option>
              </select>
              <input className="form-control form-control-sm" style={{ width:240 }}
                placeholder="Buscar destinatário ou assunto..."
                value={filtroLog.busca}
                onChange={e => setFiltroLog(p => ({ ...p, busca:e.target.value }))}/>
              <button className="btn btn-outline-secondary btn-sm" onClick={carregarLog} title="Atualizar">
                <i className="bi bi-arrow-clockwise"/>
              </button>
              <span style={{ marginLeft:"auto", fontSize:12, color:"var(--gray-500)" }}>
                {log.length} registro(s)
              </span>
            </div>

            <table className="table table-custom" style={{ marginBottom:0 }}>
              <thead>
                <tr>
                  <th>Tipo</th>
                  <th>Destinatário</th>
                  <th>Assunto</th>
                  <th>Status</th>
                  <th>Data</th>
                  <th style={{ width:60 }}></th>
                </tr>
              </thead>
              <tbody>
                {loadingLog
                  ? <tr><td colSpan={6} className="text-center py-4">
                      <span className="spinner-border spinner-border-sm text-primary"/>
                    </td></tr>
                  : !log.length
                  ? <tr><td colSpan={6} className="text-center py-4" style={{ color:"var(--gray-400)" }}>
                      Nenhum registro no log
                    </td></tr>
                  : log.map(l => (
                    <tr key={l.id}>
                      <td>
                        <span style={{
                          background:"var(--gray-100)", color:"var(--gray-600)",
                          padding:"2px 8px", borderRadius:10, fontSize:11, fontWeight:600,
                        }}>{l.tpNotificacao}</span>
                      </td>
                      <td style={{ fontSize:12.5 }}>{l.destinatario}</td>
                      <td style={{
                        fontSize:12, maxWidth:250, overflow:"hidden",
                        textOverflow:"ellipsis", whiteSpace:"nowrap",
                      }} title={l.assunto}>
                        {l.assunto}
                      </td>
                      <td>
                        <span style={{
                          background:l.status === "Enviado" ? "#DCFCE7" : l.status === "Erro" ? "#FEE2E2" : "#FEF9C3",
                          color:l.status === "Enviado" ? "#166534" : l.status === "Erro" ? "#991B1B" : "#854D0E",
                          padding:"2px 8px", borderRadius:10, fontSize:11, fontWeight:600,
                        }}>{l.status}</span>
                        {l.erroMsg && (
                          <div style={{ fontSize:10, color:"#991B1B", marginTop:2 }} title={l.erroMsg}>
                            {l.erroMsg.substring(0, 80)}{l.erroMsg.length > 80 ? "..." : ""}
                          </div>
                        )}
                      </td>
                      <td style={{ fontSize:12, color:"var(--gray-500)", whiteSpace:"nowrap" }}>
                        {l.dtEnvio ? fmtDt(l.dtEnvio) : (l.createdAt ? fmtDt(l.createdAt) : "—")}
                      </td>
                      <td>
                        {l.status === "Erro" && (
                          <button className="btn btn-outline-primary btn-sm btn-icon"
                            title="Reenviar e-mail"
                            disabled={reenviandoId === l.id}
                            onClick={() => handleReenviar(l.id)}>
                            {reenviandoId === l.id
                              ? <span className="spinner-border spinner-border-sm" style={{ width:12, height:12 }}/>
                              : <i className="bi bi-arrow-repeat"/>}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                }
              </tbody>
            </table>
          </>
        )}
      </div>
    </div>
  );
}
